import { useSortableTable } from '../../../hooks/useSortableTable';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigateWithPreference } from '../../../utils/navigationHelper';
import { apiFetch } from '../../../utils/api';
import PageHeader from '../../../components/PageHeader';

function Warehouses() {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  
  // Sorting
  const { items: sortedWarehouses, requestSort, getSortIndicator } = useSortableTable(warehouses, { key: 'name', direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef(null);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    code: true,
    name: true,
    address: true,
    city: true,
    postcode: true,
    contactName: true,
    contactPhone: true,
    contactEmail: false,
    productCount: true,
    totalItems: true,
    isDefault: true,
    isActive: true,
  });

  // Available columns configuration
  const columns = [
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Warehouse Name' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'postcode', label: 'Postcode' },
    { key: 'contactName', label: 'Contact Name' },
    { key: 'contactPhone', label: 'Contact Phone' },
    { key: 'contactEmail', label: 'Contact Email' },
    { key: 'productCount', label: 'Products' },
    { key: 'totalItems', label: 'Total Items' },
    { key: 'isDefault', label: 'Default' },
    { key: 'isActive', label: 'Active' },
  ];

  useEffect(() => {
    fetchWarehouses();
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await apiFetch('/api/preferences/default/warehouses');
      const data = await response.json();
      
      if (data.preferences) {
        setVisibleColumns(data.preferences);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const savePreferences = async (newPreferences) => {
    try {
      await apiFetch('/api/preferences/default/warehouses', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: newPreferences })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  useEffect(() => {
    savePreferences(visibleColumns);
  }, [visibleColumns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchWarehouses = async () => {
    try {
      const response = await apiFetch('/api/warehouses');
      const data = await response.json();
      setWarehouses(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      setLoading(false);
    }
  };

  const handleWarehouseAdded = () => {
    fetchWarehouses();
    setShowAddModal(false);
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const filteredWarehouses = sortedWarehouses.filter(warehouse =>
    warehouse.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    warehouse.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    warehouse.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length + 1;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="Warehouses"
        subtitle={`${warehouses.length} total`}
        backButton
        onBack={() => navigate('/inventory-landing')}
        backTitle="Back to Inventory"
        sticky
        actionButton={
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            ➕ Add a Warehouse
          </button>
        }
      />

      <main className="max-w-full mx-auto px-6 py-8">
        {/* Search and Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search warehouses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="relative" ref={columnMenuRef}>
              <button
                onClick={() => setShowColumnMenu(!showColumnMenu)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center gap-2"
              >
                <span>⚙️</span>
                <span>Columns</span>
              </button>
              
              {showColumnMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-700">Show/Hide Columns</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {columns.map(column => (
                      <label
                        key={column.key}
                        className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns[column.key]}
                          onChange={() => toggleColumn(column.key)}
                          className="mr-3 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{column.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-gray-400">Loading warehouses...</div>
          </div>
        ) : filteredWarehouses.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">🏢</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No warehouses found</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm ? 'Try adjusting your search' : 'Get started by adding your first warehouse'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
              >
                ➕ Add a Warehouse
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {visibleColumns.code && (
                      <th 
                        onClick={() => requestSort('code')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          Code
                          {getSortIndicator('code')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.name && (
                      <th 
                        onClick={() => requestSort('name')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          Warehouse Name
                          {getSortIndicator('name')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.address && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Address
                      </th>
                    )}
                    {visibleColumns.city && (
                      <th 
                        onClick={() => requestSort('city')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          City
                          {getSortIndicator('city')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.postcode && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Postcode
                      </th>
                    )}
                    {visibleColumns.contactName && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact Name
                      </th>
                    )}
                    {visibleColumns.contactPhone && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phone
                      </th>
                    )}
                    {visibleColumns.contactEmail && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                    )}
                    {visibleColumns.productCount && (
                      <th 
                        onClick={() => requestSort('product_count')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          Products
                          {getSortIndicator('product_count')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.totalItems && (
                      <th 
                        onClick={() => requestSort('total_items')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          Total Items
                          {getSortIndicator('total_items')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.isDefault && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Default
                      </th>
                    )}
                    {visibleColumns.isActive && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredWarehouses.map((warehouse, index) => (
                    <tr 
                      key={warehouse.id}
                      className={index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}
                    >
                      {visibleColumns.code && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono text-sm font-semibold text-blue-600">
                            {warehouse.code}
                          </span>
                        </td>
                      )}
                      {visibleColumns.name && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{warehouse.name}</div>
                        </td>
                      )}
                      {visibleColumns.address && (
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{warehouse.address || '—'}</div>
                        </td>
                      )}
                      {visibleColumns.city && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{warehouse.city || '—'}</div>
                        </td>
                      )}
                      {visibleColumns.postcode && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{warehouse.postcode || '—'}</div>
                        </td>
                      )}
                      {visibleColumns.contactName && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{warehouse.contact_name || '—'}</div>
                        </td>
                      )}
                      {visibleColumns.contactPhone && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{warehouse.contact_phone || '—'}</div>
                        </td>
                      )}
                      {visibleColumns.contactEmail && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{warehouse.contact_email || '—'}</div>
                        </td>
                      )}
                      {visibleColumns.productCount && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">{warehouse.product_count || 0}</span>
                        </td>
                      )}
                      {visibleColumns.totalItems && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">{warehouse.total_items || 0}</span>
                        </td>
                      )}
                      {visibleColumns.isDefault && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {warehouse.is_default && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ✓ Default
                            </span>
                          )}
                        </td>
                      )}
                      {visibleColumns.isActive && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            warehouse.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {warehouse.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => navigateWithPreference(navigate, `/warehouse/${warehouse.id}`)}
                          className="text-blue-600 hover:text-blue-900 font-medium"
                        >
                          View →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Table Footer with Result Count */}
            <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{filteredWarehouses.length}</span> of <span className="font-medium">{warehouses.length}</span> warehouses
                {searchTerm && <span> (filtered)</span>}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Add Warehouse Modal - Placeholder */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Add New Warehouse</h2>
            <p className="text-gray-600 mb-4">Add warehouse form coming soon...</p>
            <button
              onClick={() => setShowAddModal(false)}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Warehouses;
